// French for the contracting page: the verdict, the readiness ladder, the document, the payment
// schedule, change requests, the record (baselines, diff, signature, clause library) and every
// modal. Registered on load. Keys are the English text as written in ContractingPage.tsx.
import { registerFr } from '../i18n/index.js';

registerFr({
  // ── small shared bits ──────────────────────────────────────────────────────
  'Understood': 'Compris',
  'Nothing else points at it — the row goes on its own.': 'Rien d’autre n’y renvoie — la ligne s’en va seule.',
  'This also removes:': 'Ceci supprime également :',
  'Cancel': 'Annuler',
  'Save': 'Enregistrer',
  'Edit': 'Modifier',
  'Add': 'Ajouter',
  'New': 'Nouveau',
  'Delete': 'Supprimer',
  'Title': 'Titre',
  'Status': 'Statut',
  'of': 'sur',
  'on': 'le',
  'or': 'ou',
  'from': 'de',
  'due': 'échéance',
  'no date': 'sans date',
  'd': 'j',
  'rev': 'Rév.',
  'ID': 'ID',
  'Hash': 'Hash',
  'Ref': 'Réf.',
  'Detail': 'Détail',
  'Change': 'Modification',
  'Total': 'Total',

  // ── status, priority and type tokens as displayed ──────────────────────────
  'draft': 'brouillon',
  'issued': 'émis',
  'signed': 'signé',
  'superseded': 'remplacé',
  'submitted': 'soumis',
  'approved': 'approuvé',
  'rejected': 'refusé',
  'deferred': 'reporté',
  'withdrawn': 'retiré',
  'planned': 'planifié',
  'in progress': 'en cours',
  'delivered': 'livré',
  'accepted': 'accepté',
  'invoiced': 'facturé',
  'confirmed': 'confirmé',
  'baselined': 'dans la baseline',
  'descoped': 'retiré(e) du périmètre',
  'dropped': 'abandonné',
  'must': 'Doit',
  'should': 'Devrait',
  'could': 'Pourrait',
  'wont': 'Exclu',
  'functional': 'fonctionnel',
  'non functional': 'non fonctionnel',
  'constraint': 'contrainte',
  'assumption': 'hypothèse',
  'out of scope': 'hors périmètre',
  'added': 'ajouté',
  'changed': 'modifié',
  'removed': 'supprimé',
  'modify': 'modifier',
  'create': 'créer',
  'descope': 'retirer du périmètre',

  // ── toasts and error fallbacks ─────────────────────────────────────────────
  'The pre-flight did not answer.': 'Le contrôle préalable n’a pas répondu.',
  'Failed to load contracting metadata': 'Impossible de charger les métadonnées du mandat',
  'Failed to load the clause library': 'Impossible de charger la bibliothèque de clauses',
  'Failed to load the SOW version': 'Impossible de charger la version de la SOW',
  'Re-rendered from the register · {hash}': 'Re-rendu depuis le registre · {hash}',
  'Could not save the draft': 'Impossible d’enregistrer le brouillon',
  'Version {n} rendered from the live register': 'Version {n} rendue depuis le registre courant',
  'Could not create the version': 'Impossible de créer la version',
  'Could not create the SOW': 'Impossible de créer la SOW',
  'Could not check the version before issuing': 'Impossible de vérifier la version avant l’émission',
  'Issued.': 'Émis.',
  'The register had moved — the issued document reflects today.': 'Le registre avait bougé — le document émis reflète l’état du jour.',
  'Could not issue the version': 'Impossible d’émettre la version',
  'Signature recorded.': 'Signature enregistrée.',
  'Could not record the signature': 'Impossible d’enregistrer la signature',
  '{key} staged — it goes in when the document is created': '{key} prévu — il sera repris à la création du document',
  '{key} written · re-rendered {hash}': '{key} écrit · re-rendu {hash}',
  'Could not write the value': 'Impossible d’écrire la valeur',
  'Could not work out what deleting this would do': 'Impossible de déterminer l’effet de cette suppression',
  '{name} deleted': '{name} supprimé(e)',
  'Could not delete': 'Suppression impossible',
  'Milestone added': 'Jalon ajouté',
  'Could not add the milestone': 'Impossible d’ajouter le jalon',
  'Milestone updated': 'Jalon mis à jour',
  'Could not update the milestone': 'Impossible de mettre à jour le jalon',
  'Could not reorder': 'Impossible de réordonner',
  'Could not load the change request': 'Impossible de charger la demande de modification',
  '{ref} impact updated': 'Impact de {ref} mis à jour',
  'Could not save the impact': 'Impossible d’enregistrer l’impact',
  '{ref} submitted to the client — uncosted, on your confirmation': '{ref} soumise au client — non chiffrée, sur votre confirmation',
  '{ref} submitted to the client': '{ref} soumise au client',
  'Could not submit {ref}': 'Impossible de soumettre {ref}',
  '{ref} created as a draft': '{ref} créée en brouillon',
  'Could not create the change request': 'Impossible de créer la demande de modification',
  '{ref} → {status}': '{ref} → {status}',
  'Could not move {ref} to {status}': 'Impossible de faire passer {ref} à {status}',
  'Could not load the register': 'Impossible de charger le registre',
  '{ref} approved · {n} revision(s) applied, addendum v{v} drafted': '{ref} approuvée · {n} révision(s) appliquée(s), avenant v{v} mis en brouillon',
  '{ref} approved · {n} revision(s) applied': '{ref} approuvée · {n} révision(s) appliquée(s)',
  'Could not approve the change request': 'Impossible d’approuver la demande de modification',
  'Could not compute the diff': 'Impossible de calculer la comparaison',
  'Clause {key} v{v} created': 'Clause {key} v{v} créée',
  '{key} is now v{v} — signed SOWs keep the version they printed': '{key} est désormais en v{v} — les SOW signées conservent la version qu’elles ont imprimée',
  'Could not save the clause': 'Impossible d’enregistrer la clause',
  '{key} retired': '{key} désactivé(e)',
  '{key} restored': '{key} rétabli(e)',
  'Could not change the clause': 'Impossible de modifier la clause',

  // ── the readiness ladder ───────────────────────────────────────────────────
  'Register →': 'Registre →',
  'Schedule ↓': 'Échéancier ↓',
  'Fill ↓': 'Remplir ↓',
  'the register speaking': 'le registre parle',
  'the generator speaking': 'le générateur parle',
  'Linter:': 'Linter :',
  'Fails the acceptance-criterion linter.': 'Échoue au linter des critères d’acceptation.',
  'Go to the payment schedule ↓': 'Aller à l’échéancier de paiement ↓',
  'unverifiable ACs': 'AC non vérifiables',
  'acceptance criterion is not verifiable as written': 'critère d’acceptation n’est pas vérifiable tel qu’il est rédigé',
  'acceptance criteria are not verifiable as written': 'critères d’acceptation ne sont pas vérifiables tels qu’ils sont rédigés',
  'Issuing puts Annex A into the contract. A criterion the linter rejects cannot be proved met by anybody — not the client at UAT, not you at acceptance. The server refuses to issue until these are fixed, or until you say out loud that they go in as written.':
    'L’émission fait entrer l’Annexe A dans le contrat. Un critère que le linter rejette ne peut être prouvé rempli par personne — ni par le client à l’UAT, ni par vous à l’acceptation. Le serveur refuse d’émettre tant que ces critères ne sont pas corrigés, ou tant que vous n’avez pas dit explicitement qu’ils entrent tels quels.',
  'Open each criterion in the register and add the number, count or duration a tester checks. Then re-save the draft.':
    'Ouvrez chaque critère dans le registre et ajoutez le nombre, la quantité ou la durée qu’un testeur vérifie. Réenregistrez ensuite le brouillon.',
  'Open {ref} in the Register →': 'Ouvrir {ref} dans le Registre →',
  'the criterion': 'le critère',
  'no exclusions': 'aucune exclusion',
  'Nothing is expressly out of scope': 'Rien n’est expressément hors périmètre',
  'The contract prints an': 'Le contrat imprime toujours une section',
  'Out of scope': 'Hors périmètre',
  'section whatever happens. With no requirement typed': '— quelle que soit la situation. Sans exigence de type',
  'and none descoped, that section reads': 'ni rien retiré du périmètre, cette section affiche',
  '— the one page a scope dispute is decided on, blank. Exclusions are first-class in the schema precisely so they can be printed.':
    '— l’unique page où se tranche un différend de périmètre, reste vide. Les exclusions sont des éléments de premier ordre du schéma, précisément pour qu’elles puissent être imprimées.',
  'What the section will print': 'Ce que la section imprimera',
  'Type the things you discussed and are NOT doing as requirements of type': 'Saisissez ce que vous avez discuté et ne ferez PAS comme exigences de type',
  'They print as the exclusions list.': 'Elles s’impriment comme liste des exclusions.',
  'New out-of-scope requirement →': 'Nouvelle exigence hors périmètre →',
  'Assumptions': 'Hypothèses',
  'Constraints': 'Contraintes',
  'empty sections': 'sections vides',
  'priced-on section will print empty — {names}': 'section déterminante pour le prix s’imprimera vide — {names}',
  'priced-on sections will print empty — {names}': 'sections déterminantes pour le prix s’imprimeront vides — {names}',
  'These sections tell the client what the price assumed and what bound the solution. Empty, they hand back every assumption you actually made. They are populated by typing requirements as':
    'Ces sections indiquent au client ce que le prix a supposé et ce qui a cadré la solution. Vides, elles annulent toute hypothèse que vous avez réellement posée. Elles se remplissent en saisissant des exigences comme',
  'in the register — no separate form exists, and none should.': 'dans le registre — il n’existe pas de formulaire séparé, et il ne devrait pas y en avoir.',
  'Register composition': 'Composition du registre',
  'no requirements': 'aucune exigence',
  'counted, not judged': 'comptés, non jugés',
  'Same surface, different type:': 'Même surface, type différent :',
  'for what the price assumed,': 'pour ce que le prix a supposé,',
  'for what was fixed.': 'pour ce qui était fixé.',
  'Open the register →': 'Ouvrir le registre →',
  'unfunded milestone': 'jalon sans livrable',
  'payment milestone carries money but no deliverable': 'le jalon de paiement porte un montant mais aucun livrable',
  'payment milestones carry money but no deliverable': 'les jalons de paiement portent un montant mais aucun livrable',
  'An acceptance record is issued': 'Un procès-verbal d’acceptation est émis',
  'for a milestone': 'pour un jalon',
  'and lists the deliverables it accepts. A milestone with an amount and no deliverable is an invoice trigger with nothing to trigger it — the client is asked to pay on the acceptance of nothing.':
    'et liste les livrables qu’il accepte. Un jalon avec un montant et sans livrable est un déclencheur de facturation qui n’a rien à déclencher — on demande au client de payer l’acceptation de rien.',
  'visible to the client': 'visible pour le client',
  'deliverables': 'livrables',
  'acceptance records': 'procès-verbaux d’acceptation',
  'counted': 'comptés',
  'Either attach a deliverable to the milestone, or fold its amount into a milestone that has one. A payment needs something to accept.':
    'Attachez un livrable au jalon, ou reportez son montant sur un jalon qui en a un. Un paiement a besoin de quelque chose à accepter.',
  'blank variables': 'variables vides',
  'contract variable the record can fill': 'variable contractuelle que le dossier peut renseigner',
  'contract variables the record can fill': 'variables contractuelles que le dossier peut renseigner',
  '{n} only you can': '{n} vous seul',
  'A new version renders with every variable outside the defaults blank. These values are already columns in this database; retyping them is how a signature block ends up naming a company where it should name a person.':
    'Une nouvelle version se rend avec toute variable hors des valeurs par défaut laissée vide. Ces valeurs sont déjà des colonnes de cette base de données ; les retaper est la façon dont un bloc de signature finit par nommer une entreprise là où il devrait nommer une personne.',
  'staged': 'prévu',
  'in the document': 'dans le document',
  'v{n} is frozen': 'v{n} est figée',
  'Use': 'Utiliser',
  'No column holds these — you type them:': 'Aucune colonne ne les contient — vous les saisissez :',
  'Each value above names the column it came from. Accepting one copies it into': 'Chaque valeur ci-dessus nomme la colonne dont elle provient. L’accepter la copie dans',
  '— nothing is inferred and nothing is typed twice.': '— rien n’est déduit et rien n’est saisi deux fois.',
  'value mismatch': 'écart de valeur',
  'Milestone total {total} matches the engagement estimate': 'Le total des jalons {total} correspond à l’estimation du mandat',
  'Milestone total {total} does not match the engagement estimate {estimate}': 'Le total des jalons {total} ne correspond pas à l’estimation du mandat {estimate}',
  'The figure the SOW prints and the figure gate G2 reads are the same number.': 'Le montant que la SOW imprime et celui que lit le gate G2 sont le même nombre.',
  'G2 reads whichever is present; the SOW prints the milestone sum. Two different numbers describe this engagement and the contract will print one of them without saying so.':
    'G2 lit celui qui est présent ; la SOW imprime la somme des jalons. Deux montants différents décrivent ce mandat, et le contrat en imprimera un sans le préciser.',
  'Both figures': 'Les deux montants',
  'milestones sum': 'somme des jalons',
  'added, not judged': 'additionnés, non jugés',
  'Decide which number is true and make the other match. The contract prints the milestone sum; G2 reads whichever it finds.':
    'Décidez quel chiffre est correct et alignez l’autre. Le contrat imprime la somme des jalons ; G2 lit celui qu’il trouve.',
  'draft requirements': 'exigences en brouillon',
  'of {total} requirements are still draft': 'sur {total} exigences sont encore en brouillon',
  '{total} of {total} requirements held': '{total} sur {total} exigences retenues',
  'A draft requirement prints into the scope table exactly like a held one. G1 has not been cleared for these.':
    'Une exigence en brouillon s’imprime dans le tableau de périmètre exactement comme une exigence retenue. G1 n’est pas dégagé pour celles-ci.',
  'Every row the scope section will print has been committed by a human.': 'Chaque ligne que la section périmètre imprimera a été validée par une personne.',
  'Scope table, as it will print': 'Tableau de périmètre, tel qu’il s’imprimera',
  'Confirm them, or drop them. A draft row prints into the scope table looking exactly like a committed one.':
    'Confirmez-les, ou abandonnez-les. Une ligne en brouillon s’imprime dans le tableau de périmètre en ayant exactement l’air d’une ligne validée.',
  'Open the register, filtered to draft →': 'Ouvrir le registre, filtré sur brouillon →',

  // ── the rail ───────────────────────────────────────────────────────────────
  'DOCUMENT': 'DOCUMENT',
  'WOULD PRINT': 'IMPRIMERAIT',
  'VALUE': 'VALEUR',
  'NOT FIT TO SIGN': 'PAS PRÊT À SIGNER',
  'G3 · SOW SIGNED': 'G3 · SOW SIGNÉE',
  'no SOW exists': 'aucune SOW n’existe',
  '{n} in scope': '{n} dans le périmètre',
  '{must} must · {excluded} excluded': '{must} Doit · {excluded} hors périmètre',
  'the pre-flight did not answer': 'le contrôle préalable n’a pas répondu',
  'no estimate on record': 'aucune estimation enregistrée',
  '= engagement estimate': '= estimation du mandat',
  'estimate {estimate}': 'estimation {estimate}',
  'nothing outstanding': 'rien en suspens',
  '{met} of {total}': '{met} sur {total}',
  '{autoMet}/{autoTotal} auto · {manMet}/{manTotal} manual': '{autoMet}/{autoTotal} auto · {manMet}/{manTotal} manuel',
  'gate engine did not answer': 'le moteur de gate n’a pas répondu',
  'document': 'Document',
  'register': 'Registre',
  'schedule': 'Échéancier',
  'the list': 'la liste',
  'lifecycle': 'Cycle de vie',
  'Open the {target}': 'Ouvrir {target}',

  // ── the pipeline and the section map ───────────────────────────────────────
  'Register': 'Registre',
  'Draft': 'Brouillon',
  'Issued': 'Émis',
  'Signed': 'Signé',
  'Baseline': 'Baseline',
  'not created': 'non créé',
  'no contact nominated': 'aucun contact désigné',
  'set': 'défini',
  'no background recorded': 'aucun contexte enregistré',
  '{count} requirements · {groups}': '{count} exigences · {groups}',
  '{n} group': '{n} groupe',
  '{n} groups': '{n} groupes',
  '{n} excluded': '{n} hors périmètre',
  '{n} deliverable': '{n} livrable',
  '{n} deliverables': '{n} livrables',
  '{n} milestone': '{n} jalon',
  '{n} milestones': '{n} jalons',
  '{n} working days': '{n} jours ouvrables',
  '{n} criteria · {bad} unverifiable': '{n} critères · {bad} non vérifiables',
  '{n} criteria': '{n} critères',
  'named': 'nommé',
  'client name in place of a person': 'nom du client à la place d’une personne',

  // ── masthead and verdict ───────────────────────────────────────────────────
  'Contracting': 'Contractualisation',
  'No engagement selected': 'Aucun mandat sélectionné',
  'Pick an engagement in the top bar — a Statement of Work is always scoped to one.': 'Choisissez un mandat dans la barre supérieure — un descriptif des prestations (SOW) se rapporte toujours à un seul mandat.',
  'contracting as {name}': 'Partie contractante : {name}',
  'Reading the register…': 'Lecture du registre…',
  'The pre-flight could not be computed, so this page cannot say whether the register is fit to print a contract.':
    'Le contrôle préalable n’a pas pu être calculé ; cette page ne peut donc pas dire si le registre est prêt à imprimer un contrat.',
  'The register is not yet fit to print a contract —': 'Le registre n’est pas encore prêt à imprimer un contrat —',
  'hard stop,': 'point bloquant,',
  'hard stops,': 'points bloquants,',
  'thing worth fixing first.': 'point à corriger en priorité.',
  'things worth fixing first.': 'points à corriger en priorité.',
  'You can create the document now; the server will refuse to': 'Vous pouvez créer le document maintenant ; le serveur refusera de',
  'issue': 'l’émettre',
  'it while the hard stops stand.': 'tant que les points bloquants subsistent.',
  'No Statement of Work exists. The register would print': 'Aucun descriptif des prestations (SOW) n’existe. Le registre imprimerait',
  'requirements into one.': 'exigences dans un premier document.',
  'is a draft. Nothing has left the building.': 'est un brouillon. Rien n’est encore sorti.',
  'Issuing re-renders without the draft marker, publishes to the portal and writes the PDF.': 'L’émission re-rend le document sans la marque de brouillon, le publie sur le portail et génère le PDF.',
  'is with the client, issued {when}. It is the hash they will sign:': 'est chez le client, émis le {when}. Voici le hash à signer :',
  'Recording the signature freezes the register as an immutable baseline. After that, scope moves only through a change request.':
    'L’enregistrement de la signature fige le registre en une baseline immuable. Ensuite, le périmètre ne bouge plus que par une demande de modification.',
  'by {name}': 'par {name}',
  'holds': 'comprend',
  '{n} requirement': '{n} exigence',
  '{n} requirements': '{n} exigences',
  'G3 is cleared on the record. The only route through the baseline is a change request.': 'G3 est validé, consigné au dossier. Le seul chemin à travers la baseline est une demande de modification.',
  'Create the SOW': 'Créer la SOW',
  'Save the draft first — the preview is behind your edits': 'Enregistrez d’abord le brouillon — l’aperçu est en retard sur vos modifications',
  'Issue to the client': 'Émettre au client',
  'Record the signature': 'Enregistrer la signature',
  'Raise a change request': 'Déposer une demande de modification',

  // ── zone 1 · fit to sign ───────────────────────────────────────────────────
  'Fit to sign': 'Prêt à signer',
  'Fit to sign — v{n} draft': 'Prêt à signer — v{n} brouillon',
  'Fit to sign — what a v{n} would carry': 'Prêt à signer — ce qu’une v{n} contiendrait',
  'Fit to sign — v{n} is frozen; this is what an addendum would carry': 'Prêt à signer — v{n} est figée ; voici ce qu’un avenant contiendrait',
  'every row states its own evidence and routes to the surface that owns the fix': 'chaque ligne indique sa propre preuve et renvoie vers la surface à laquelle appartient la correction',
  'The gate tallies': 'Les décomptes du gate',
  'The pre-flight did not answer': 'Le contrôle préalable n’a pas répondu',
  'GET /api/contracting/readiness failed.': 'GET /api/contracting/readiness a échoué.',
  'blocks issuing — the server refuses': 'bloque l’émission — le serveur refuse',
  'block issuing — the server refuses': 'bloquent l’émission — le serveur refuse',
  'worth fixing — nothing stops you': 'à corriger — rien ne vous en empêche',
  'met': 'remplies',
  'Why it is met': 'Pourquoi rempli',
  'The fix': 'La correction',
  'Nothing to do — this condition is satisfied by the rows on the left.': 'Rien à faire — cette condition est satisfaite par les lignes de gauche.',
  'conditions met': 'conditions remplies',

  // ── zone 2 · the document ──────────────────────────────────────────────────
  'The document': 'Le document',
  '{n} section override': '{n} surcharge de section',
  '{n} section overrides': '{n} surcharges de section',
  'not created — this is what it would contain': 'non créé — voici ce qu’il contiendrait',
  'Versions': 'Versions',
  'addendum': 'avenant',
  'Variables': 'Variables',
  'have a value': 'ont une valeur',
  'narrative': 'Narratif',
  'commercial': 'Commercial',
  'parties': 'Parties',
  '{set} of {total}': '{set} sur {total}',
  'blank — the record offers a value in': 'vide — le dossier propose une valeur dans',
  'blank — the record offers a value ↑': 'vide — le dossier propose une valeur ↑',
  'blank — the signature block degrades without it': 'vide — le bloc de signature se dégrade sans cette valeur',
  'Save & re-render': 'Enregistrer et re-rendre',
  'Unsaved edits — the preview still shows the last render.': 'Modifications non enregistrées — l’aperçu affiche encore le dernier rendu.',
  'Clauses': 'Clauses',
  '{n} pinned at the version this document printed': '{n} épinglée(s) à la version imprimée par ce document',
  '{n} would print, at their latest version': '{n} seraient imprimées, dans leur dernière version',
  'v{n} available': 'v{n} disponible',
  'Re-pin to latest': 'Réépingler sur la dernière version',
  'Edit the library →': 'Modifier la bibliothèque →',
  'A signed SOW keeps the clause version it printed.': 'Une SOW signée conserve la version de clause qu’elle a imprimée.',
  'Sections': 'Sections',
  'what each will print': 'ce que chacune imprimera',
  'An override replaces the generated section entirely and is flagged on every regeneration. Clear the box to go back to the projection.':
    'Une surcharge remplace entièrement la section générée et est signalée à chaque nouvelle génération. Videz le champ pour revenir à la projection.',
  'overridden': 'surchargée',
  'Leave empty to use the generated text.': 'Laissez vide pour utiliser le texte généré.',
  'Document preview': 'Aperçu du document',
  'rendered from the register · this HTML is canonical · paper in both themes, because it prints': 'rendu depuis le registre · ce HTML fait foi · papier dans les deux thèmes, car il est destiné à l’impression',
  'open HTML': 'Ouvrir le HTML',
  'Rendered without the draft marker or the lint notes — what the client would receive': 'Rendu sans la marque de brouillon ni les remarques du linter — ce que le client recevrait',
  'as the client sees it': 'tel que le client le voit',
  'baseline': 'baseline',
  'New version from register': 'Nouvelle version depuis le registre',
  'Delete this draft — it has never been issued': 'Supprimer ce brouillon — il n’a jamais été émis',
  'Draft v{n}': 'Brouillon v{n}',
  'Discard draft': 'Abandonner le brouillon',
  'Not rendered yet.': 'Pas encore rendu.',
  'No document has been rendered yet. The section map on the left is what a first version would print, read from the live register — the same projection the generator uses. Creating the SOW renders it and nothing else moves.':
    'Aucun document n’a encore été rendu. La carte des sections à gauche montre ce qu’imprimerait une première version, lue depuis le registre courant — la même projection qu’utilise le générateur. La création de la SOW la rend, et rien d’autre ne bouge.',

  // ── zone 3 · payment schedule ──────────────────────────────────────────────
  'Payment schedule': 'Échéancier de paiement',
  'across': 'sur',
  '— the figure the SOW prints and the figure G2 reads': '— le montant que la SOW imprime et celui que G2 lit',
  'The payment schedule did not load': 'L’échéancier de paiement n’a pas été chargé',
  'No milestones yet': 'Pas encore de jalons',
  'G2 needs at least one priced milestone as the estimate, and the SOW prints this table as its payment schedule.':
    'G2 a besoin d’au moins un jalon chiffré comme estimation, et la SOW imprime ce tableau comme échéancier de paiement.',
  'Add the first milestone': 'Ajouter le premier jalon',
  'Milestone': 'Jalon',
  'Due': 'Échéance',
  'Amount': 'Montant',
  'Accepts': 'Accepte',
  'Client': 'Client',
  'Milestone name': 'Nom du jalon',
  'Due date': 'Date d’échéance',
  'visible': 'visible',
  'hidden': 'masqué',
  'Move up': 'Monter',
  'Move down': 'Descendre',
  'deliverable': 'livrable',
  'money, nothing to accept': 'montant, rien à accepter',
  'Delete milestone {name}': 'Supprimer le jalon {name}',
  'Delete this milestone — refused once it is in the signed record': 'Supprimer ce jalon — refusé dès qu’il figure dans le dossier signé',
  'says': 'dit',
  'New milestone name': 'Nom du nouveau jalon',
  'New milestone due date': 'Date d’échéance du nouveau jalon',
  'amount': 'montant',
  'New milestone amount': 'Montant du nouveau jalon',
  'Add a milestone': 'Ajouter un jalon',

  // ── zone 4 · change requests ───────────────────────────────────────────────
  'Change requests': 'Demandes de modification',
  'open': 'ouvertes',
  'the only route through baseline': 'le seul chemin à travers la baseline',
  'no baseline yet — nothing is frozen, so these are still ordinary scope edits': 'pas encore de baseline — rien n’est figé, ce sont donc encore de simples modifications de périmètre',
  'New change request': 'Nouvelle demande de modification',
  'No change requests': 'Aucune demande de modification',
  'The baseline is frozen and nothing has been asked for since. Every scope move from here raises one.': 'La baseline est figée et rien n’a été demandé depuis. Tout mouvement de périmètre à partir d’ici en soulève une.',
  'revised {refs}': 'a révisé {refs}',
  'no charge': 'gratuit',
  'uncosted': 'non chiffré',
  'goodwill': 'geste commercial',

  // ── the record ─────────────────────────────────────────────────────────────
  'The record‖contract': 'Le dossier',
  '{n} baseline': '{n} baseline',
  '{n} baselines': '{n} baselines',
  '{n} SOW version': '{n} version SOW',
  '{n} SOW versions': '{n} versions SOW',
  '{n} clause': '{n} clause',
  '{n} clauses': '{n} clauses',
  'in the library': 'dans la bibliothèque',
  'Baselines': 'Baselines',
  'immutable snapshots — the event that freezes the register': 'instantanés immuables — l’événement qui fige le registre',
  'No baseline yet. One is created the moment a SOW version is signed — that is the event that freezes the register.':
    'Pas encore de baseline. Une baseline est créée au moment où une version de la SOW est signée — c’est l’événement qui fige le registre.',
  'Label': 'Libellé',
  'Created': 'Créé',
  'Requirements': 'Exigences',
  'Deliverables': 'Livrables',
  'Compare to live': 'Comparer au registre courant',
  'What has moved': 'Ce qui a bougé',
  '{n} record': '{n} élément',
  '{n} records': '{n} éléments',
  'differ': 'diffèrent',
  'have moved': 'ont bougé',
  'between {from} and {to}': 'entre {from} et {to}',
  '{n} under {ref}': '{n} sous {ref}',
  'pick two points and compare': 'choisissez deux points et comparez',
  'From': 'De',
  'To': 'À',
  'Live register': 'Registre courant',
  'Compare': 'Comparer',
  'Print / export': 'Imprimer / exporter',
  'requirements': 'exigences',
  'acceptance criteria': 'critères d’acceptation',
  'milestones': 'jalons',
  'identical, no changes': 'identique, aucun changement',
  'identical': 'identique',
  'Signature on record': 'Signature enregistrée',
  'no ip recorded': 'aucune IP enregistrée',
  'no agent recorded': 'aucun user-agent enregistré',
  'Clause library': 'Bibliothèque de clauses',
  'shared across every engagement — an edit creates the NEXT version': 'partagée entre tous les mandats — une modification crée la version SUIVANTE',
  'Library': 'Bibliothèque',
  'retired': 'désactivée',
  'New clause': 'Nouvelle clause',
  'Revise {key}': 'Réviser {key}',
  'Select a clause': 'Sélectionner une clause',
  'Retire': 'Désactiver',
  'Restore': 'Rétablir',
  'Saving creates the NEXT version. Signed SOWs keep the version they printed — nothing already agreed changes retroactively.':
    'L’enregistrement crée la version SUIVANTE. Les SOW signées conservent la version qu’elles ont imprimée — rien de déjà convenu ne change rétroactivement.',
  'Key': 'Clé',
  'e.g. subcontracting': 'p. ex. subcontracting',
  'Body — markdown: paragraphs, **bold**, - bullets': 'Texte — Markdown : paragraphes, **gras**, - puces',
  'Create clause': 'Créer la clause',
  'Save as new version': 'Enregistrer comme nouvelle version',
  'Usage': 'Utilisation',
  'SOW version(s) cite this clause': 'version(s) de SOW citent cette clause',
  'on a superseded version': 'sur une version remplacée',
  'of those are SIGNED and keep the old wording': 'd’entre elles sont SIGNÉES et conservent l’ancien libellé',
  'Engagement': 'Mandat',
  'Version': 'Version',
  'Clause': 'Clause',
  'stale': 'obsolète',
  'Pick a clause to read it, revise it or see which SOW versions cite it.': 'Sélectionnez une clause pour la lire, la réviser ou voir quelles versions de SOW la citent.',

  // ── the change-request panel ───────────────────────────────────────────────
  'visible to client': 'visible pour le client',
  'What was asked for': 'Ce qui a été demandé',
  'origin not recorded': 'origine non enregistrée',
  'raised': 'déposée le',
  'Nothing was written down. A change request with no description is a title and a price.': 'Rien n’a été noté. Une demande de modification sans description n’est qu’un titre et un prix.',
  'Impact': 'Impact',
  'Edit impact': 'Modifier l’impact',
  'Cost ({currency})': 'Coût ({currency})',
  'Schedule (days)': 'Délai (jours)',
  'Risk and dependencies': 'Risques et dépendances',
  'What this change puts at risk, and what it depends on.': 'Ce que ce changement met en péril, et ce dont il dépend.',
  'Goodwill — absorbed at no charge, still on the record': 'Geste commercial — pris en charge sans frais, mais reste consigné au dossier',
  'Save impact': 'Enregistrer l’impact',
  'Cost': 'Coût',
  'not costed': 'non chiffré',
  'Schedule': 'Délai',
  'day(s)': 'jour(s)',
  'no schedule impact recorded': 'aucun impact sur le délai enregistré',
  'Uncosted.': 'Non chiffré.',
  'Submitting publishes it to the portal, where the sponsor is asked to approve a number nobody has written down.':
    'Le soumettre le publie sur le portail, où le sponsor est invité à approuver un chiffre que personne n’a noté.',
  'It was decided without one.': 'Elle a été tranchée sans impact chiffré.',
  'Decision': 'Décision',
  'by {name} (client)': 'par {name} (client)',
  'recorded by {name}': 'enregistré par {name}',
  'A decided change request is part of the record; its impact is frozen with the decision.': 'Une demande de modification tranchée fait partie du dossier ; son impact est figé avec la décision.',
  'Register revisions this produced': 'Révisions du registre qui en résultent',
  'carries': 'porte',
  ', which is what let it through the baseline lock': ', ce qui l’a fait franchir le verrou de la baseline',
  'Notes': 'Notes',
  'system': 'Système',
  'Submit to client': 'Soumettre au client',
  'Approve and apply': 'Approuver et appliquer',
  'Defer': 'Reporter',
  'Reject': 'Refuser',
  'Withdraw': 'Retirer',
  'Delete this change request — refused once it has revised the register': 'Supprimer cette demande de modification — refusé dès qu’elle a révisé le registre',
  'Apply further revisions': 'Appliquer d’autres révisions',

  // ── the issue modal ────────────────────────────────────────────────────────
  'Issue version {n}': 'Émettre la version {n}',
  'Checking what issuing would do…': 'Vérification de l’effet de l’émission…',
  'Hash the client signs': 'Hash que le client signe',
  'Draft preview hash': 'Hash de l’aperçu du brouillon',
  'Different by design: the draft carries the “DRAFT — not for signature” marker and the operator-only lint notes; the issued document carries neither.':
    'Différence voulue : le brouillon porte la mention «DRAFT — not for signature» et les remarques du linter réservées à l’opérateur ; le document émis ne porte ni l’une ni l’autre.',
  'The register has moved since this draft was rendered.': 'Le registre a bougé depuis que ce brouillon a été rendu.',
  "Issuing re-reads it, so the client gets today's register — not what the preview shows. Re-save the draft first if you want to see exactly what goes out.":
    'L’émission le relit, si bien que le client reçoit le registre du jour — pas ce que montre l’aperçu. Réenregistrez d’abord le brouillon si vous voulez voir exactement ce qui sera envoyé.',
  '{n} acceptance criterion/criteria in Annex A are not verifiable as written.': '{n} critère(s) d’acceptation de l’Annexe A ne sont pas vérifiables tels qu’ils sont rédigés.',
  'Once issued they are contractual, and nobody — not the client, not you — can prove they are met. These are the same rows the pre-flight above lists.':
    'Une fois émis, ils ont valeur contractuelle, et personne — ni le client, ni vous — ne peut prouver qu’ils sont remplis. Ce sont les mêmes lignes que liste le contrôle préalable ci-dessus.',
  'I understand these acceptance criteria will be': 'Je comprends que ces critères d’acceptation seront',
  'contractual as written': 'contractuels tels qu’ils sont rédigés',
  ', and I am issuing them anyway. (Otherwise: fix them on the Register page, re-save the draft, and come back.)':
    ', et je les émets tout de même. (Sinon : corrigez-les sur la page Registre, réenregistrez le brouillon, puis revenez.)',
  'The document is published to the client portal for signature and a PDF is generated. Further edits need a new version.':
    'Le document est publié sur le portail client pour signature et un PDF est généré. Toute modification ultérieure nécessite une nouvelle version.',
  'Issue and publish': 'Émettre et publier',

  // ── submit-uncosted and delete confirms ────────────────────────────────────
  'Submit {ref} uncosted?': 'Soumettre {ref} sans chiffrage ?',
  'Submit uncosted': 'Soumettre sans chiffrage',
  'has neither a cost nor a schedule impact. Submitting publishes it to the client portal, where the sponsor is asked to approve it — effectively agreeing to an unknown number.':
    'n’a ni impact de coût ni impact de délai. La soumettre la publie sur le portail client, où le sponsor est invité à l’approuver — ce qui revient à accepter un chiffre inconnu.',
  'Close this, use': 'Fermez cette fenêtre, chiffrez-la via',
  'to cost it, then submit. Continue only if the impact is genuinely nil and you want that on the record.':
    'pour la chiffrer, puis soumettez-la. Ne continuez que si l’impact est réellement nul et que vous souhaitez que cela figure au dossier.',
  'Delete {name}?': 'Supprimer {name} ?',
  'Deleting…': 'Suppression…',
  'Delete permanently': 'Supprimer définitivement',
  'Immediate and permanent — this is for something that should never have existed. If it was real and simply ended, retire it instead ({alternative}). The audit log keeps the deleted row.':
    'Immédiat et définitif — réservé à ce qui n’aurait jamais dû exister. Si c’était réel et simplement arrivé à son terme, clôturez-le plutôt ({alternative}). La piste d’audit conserve la ligne supprimée.',
  'set the status to delivered or accepted': 'passez son statut à livré ou accepté',
  'reject or withdraw it': 'refusez-la ou retirez-la',
  'issue it and supersede it later': 'émettez-la et remplacez-la plus tard',
  '{name} cannot be deleted': '{name} ne peut pas être supprimé(e)',

  // ── new SOW, sign, new CR, approve modals ──────────────────────────────────
  'New Statement of Work': 'Nouveau descriptif des prestations (SOW)',
  'Statement of Work — {name}': 'Descriptif des prestations — {name}',
  'Version 1 is rendered from the live register immediately.': 'La version 1 est immédiatement rendue depuis le registre courant.',
  'The {values} you accepted from the record ({keys}) go in with it.': 'Les {values} que vous avez repris(es) du dossier ({keys}) y entrent avec elle.',
  '{n} value': '{n} valeur',
  '{n} values': '{n} valeurs',
  'Create and render': 'Créer et rendre',
  'Record the signature on v{n}': 'Enregistrer la signature sur v{n}',
  'This freezes the baseline.': 'Cela fige la baseline.',
  'Recording the signature serialises the whole register as an immutable snapshot, hashes it, and flips every held requirement to':
    'L’enregistrement de la signature sérialise l’ensemble du registre en instantané immuable, le hache et fait passer chaque exigence retenue à',
  'After this, scope moves only through a change request.': 'Ensuite, le périmètre ne bouge plus que par une demande de modification.',
  'Document hash': 'Hash du document',
  'Client signatory — typed name, exactly as signed': 'Signataire côté client — nom saisi, exactement comme signé',
  'Date signed (optional — defaults to now)': 'Date de signature (facultatif — par défaut, maintenant)',
  'I confirm the named signatory has signed this exact document, and I understand that recording it freezes the requirements register as an immutable baseline.':
    'Je confirme que la personne signataire nommée a signé exactement ce document, et je comprends que l’enregistrer fige le registre des exigences en une baseline immuable.',
  'Record signature and baseline': 'Enregistrer la signature et fixer la baseline',
  'What is being asked for, and why': 'Ce qui est demandé, et pourquoi',
  'Origin — who asked, when': 'Origine — qui a demandé, quand',
  'Impact — cost': 'Impact — coût',
  'Impact — days': 'Impact — jours',
  'Impact — risk': 'Impact — risque',
  'Goodwill — absorbed at no charge, but recorded so the concession stays visible': 'Geste commercial — pris en charge sans frais, mais enregistré pour que la concession reste visible',
  'Create draft': 'Créer le brouillon',
  'Approve {ref} — compose the register revisions': 'Approuver {ref} — composer les révisions du registre',
  'Approval and the revisions are applied in one transaction. Every revision carries': 'L’approbation et les révisions sont appliquées en une seule transaction. Chaque révision porte',
  ', which is what lets it through the baseline lock and what answers “why did this change?” later. Pick the requirement and its current wording appears — you are editing text you can read, not a truncated line in a dropdown.':
    ', ce qui la fait franchir le verrou de la baseline et répond plus tard à «pourquoi ce changement ?». Choisissez l’exigence et son libellé actuel s’affiche — vous modifiez un texte que vous pouvez lire, pas une ligne tronquée dans une liste déroulante.',
  'Revision action': 'Action de révision',
  'Requirement': 'Exigence',
  'Pick a requirement…': 'Choisir une exigence…',
  'Remove revision': 'Supprimer la révision',
  'Current wording — this is what changes': 'Libellé actuel — c’est ce qui change',
  'Reason for removing it from scope': 'Motif du retrait du périmètre',
  'Descope reason': 'Motif de retrait',
  'Statement': 'Déclaration',
  'New requirement statement': 'Déclaration de la nouvelle exigence',
  'Edited statement (leave empty to keep the current wording)': 'Déclaration modifiée (laisser vide pour conserver le libellé actuel)',
  'Rationale': 'Justification',
  'Type': 'Type',
  'Priority': 'Priorité',
  'Category': 'Catégorie',
  'Add a revision': 'Ajouter une révision',
  'Record as goodwill — absorbed at no charge, still on the record': 'Consigner comme geste commercial — pris en charge sans frais, reste néanmoins au dossier',
  'Draft an addendum SOW version from the register after applying': 'Rédiger une version SOW en avenant depuis le registre après application',
  'Approve and apply {n} revision(s)': 'Approuver et appliquer {n} révision(s)',
}, 'ContractingPage');
